import { isChildUser, isParentUser } from 'src/utils/userRole';
import { Family, User } from 'src/types/user.types';
import {
  ANNUAL_ENTITLEMENT_ID,
  ANNUAL_ENTITLEMENT_ID_WITHOUT_TRIAL,
  ANNUAL_INSTALLMENTS_ENTITLEMENT_ID,
  MONTHLY_ENTITLEMENT_ID,
  MONTHLY_ENTITLEMENT_ID_WITHOUT_TRIAL,
  SEMI_ANNUAL_ENTITLEMENT_ID,
} from 'src/modules/purchase/purchase.constants';

export type SubscriptionStatus = 'free' | 'trial' | 'active' | 'expired';
export type SubscriptionPlan = 'monthly' | 'annual' | 'semi_annual' | 'installments' | 'none';

export type FamilyGroupProperties = {
  family_id: string;
  children_count: number;
  parents_count: number;
  subscription_status: SubscriptionStatus;
  subscription_plan: SubscriptionPlan;
  family_created_date: string;
  countries: string[];
  primary_country: string | null;
  app_languages: string[];
};

export const orderedDistinctByFrequency = (values: (string | undefined)[]): string[] => {
  const counts = new Map<string, number>();
  for (const v of values) {
    if (!v) continue;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([v]) => v);
};

export const deriveSubscriptionStatus = (family: Family, now: Date): SubscriptionStatus => {
  const isFuture = (iso?: string) => (iso ? new Date(iso) > now : false);
  if (isFuture(family.premium_ends_at)) return 'active';
  if (isFuture(family.trial_ends_at)) return 'trial';
  const everHad = !!(family.premium_ends_at || family.trial_ends_at || family.trial_started_at);
  return everHad ? 'expired' : 'free';
};

export const deriveSubscriptionPlan = (activeSubscriptionId?: string): SubscriptionPlan => {
  switch (activeSubscriptionId) {
    case MONTHLY_ENTITLEMENT_ID:
    case MONTHLY_ENTITLEMENT_ID_WITHOUT_TRIAL:
      return 'monthly';
    case SEMI_ANNUAL_ENTITLEMENT_ID:
      return 'semi_annual';
    case ANNUAL_ENTITLEMENT_ID:
    case ANNUAL_ENTITLEMENT_ID_WITHOUT_TRIAL:
      return 'annual';
    case ANNUAL_INSTALLMENTS_ENTITLEMENT_ID:
      return 'installments';
    default:
      return 'none';
  }
};

export const computeFamilyGroupProperties = (
  roster: User[],
  family: Family,
  opts: { activeSubscriptionId?: string; now: Date },
): FamilyGroupProperties => {
  const countries = orderedDistinctByFrequency(roster.map((u) => u.country));
  return {
    family_id: String(family.id),
    children_count: roster.filter((u) => isChildUser(u)).length,
    parents_count: roster.filter((u) => isParentUser(u)).length,
    subscription_status: deriveSubscriptionStatus(family, opts.now),
    subscription_plan: deriveSubscriptionPlan(opts.activeSubscriptionId),
    family_created_date: family.created_at.slice(0, 10),
    countries,
    primary_country: countries[0] ?? null,
    app_languages: orderedDistinctByFrequency(roster.map((u) => u.language)),
  };
};
